// Sprint 2 — Reorganización de configuración (navegación única desde Configuración)
import { css, cx } from "styled-system/css";
import { useEffect, useMemo, useState } from "react";
import { NavLink, Outlet, useLocation, useOutletContext } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { ChevronDown, X } from "lucide-react";
import { cf } from "@/components/brand/sfturnos-styles";

type NavItem =
  | { key: string; kind: "path"; to: string; label: string; end?: boolean }
  | { key: string; kind: "hash"; to: string; label: string; hash: string }
  | { key: string; kind: "search"; to: string; label: string; search: string };

type NavGroup = { title: string; items: NavItem[] };

type DashboardOutletContext = { plan?: string };

function buildSettingsNavGroups(plan: string): NavGroup[] {
  const showTeam = plan === "PRO" || plan === "ENTERPRISE";

  const configItems: NavItem[] = [
    { key: "general", kind: "path", to: "/dashboard/settings", label: "General", end: true },
  ];
  if (showTeam) {
    configItems.push({ key: "team", kind: "path", to: "/dashboard/team", label: "Mi equipo" });
  }
  configItems.push(
    { key: "agendas", kind: "path", to: "/dashboard/agendas", label: "Mis agendas" },
    { key: "agenda-staff", kind: "path", to: "/dashboard/settings/agenda-staff", label: "Profesionales" },
    { key: "schedule", kind: "path", to: "/dashboard/schedule", label: "Disponibilidad" },
    { key: "clients", kind: "path", to: "/dashboard/clients", label: "Clientes" },
    { key: "behavior", kind: "path", to: "/dashboard/settings/behavior", label: "Comportamiento" },
    { key: "payments", kind: "path", to: "/dashboard/payments", label: "Cobros" },
    { key: "google", kind: "path", to: "/dashboard/settings/google-calendar", label: "Google Calendar" },
    { key: "custom", kind: "path", to: "/dashboard/settings/custom-fields", label: "Campos personalizados" },
    { key: "notif", kind: "path", to: "/dashboard/notifications", label: "Notificaciones" },
  );

  return [
    { title: "CONFIGURACIÓN", items: configItems },
    {
      title: "RESERVAS",
      items: [
        { key: "appt", kind: "search", to: "/dashboard/appointments", label: "Próximas", search: "" },
        { key: "hist", kind: "search", to: "/dashboard/appointments", label: "Historial", search: "tab=history" },
        { key: "recurring", kind: "path", to: "/dashboard/recurring", label: "Recurrentes" },
        { key: "waitlist", kind: "path", to: "/dashboard/waitlist", label: "Lista de espera" },
      ],
    },
    {
      title: "CUENTA",
      items: [
        { key: "subscription", kind: "path", to: "/dashboard/subscription", label: "Suscripción" },
        { key: "support", kind: "path", to: "/dashboard/settings/support", label: "Ayuda" },
      ],
    },
    {
      title: "HERRAMIENTAS",
      items: [
        { key: "blocked", kind: "path", to: "/dashboard/schedule/blocked-dates", label: "Inhabilitar fechas" },
        { key: "qr", kind: "path", to: "/dashboard/settings/qr", label: "Código QR" },
      ],
    },
  ];
}

function itemHref(item: NavItem): string | { pathname: string; search?: string; hash?: string } {
  if (item.kind === "hash") return { pathname: item.to, hash: `#${item.hash}` };
  if (item.kind === "search") {
    return item.search ? { pathname: item.to, search: `?${item.search}` } : item.to;
  }
  return item.to;
}

function computeNavActive(
  loc: { pathname: string; hash: string; search: string },
  item: NavItem,
): boolean {
  if (item.kind === "hash") {
    return loc.pathname === item.to && loc.hash === `#${item.hash}`;
  }
  if (item.kind === "search") {
    if (loc.pathname !== item.to) return false;
    const tab = new URLSearchParams(loc.search).get("tab");
    if (item.search === "tab=history") return tab === "history";
    return tab !== "history";
  }
  if (item.to === "/dashboard/appointments") return false;
  if (item.to === "/dashboard/settings" && item.key === "general") return loc.pathname === item.to;
  return loc.pathname === item.to;
}

/** Encuentra el item activo (o el fallback por pathname) entre todos los grupos. */
function findActiveItem(
  groups: NavGroup[],
  loc: { pathname: string; hash: string; search: string },
): NavItem | null {
  const items = groups.flatMap((g) => g.items);
  for (const item of items) {
    if (computeNavActive(loc, item)) return item;
  }
  for (const item of items) {
    if (item.kind === "path" && loc.pathname === item.to && item.to !== "/dashboard/appointments") {
      return item;
    }
  }
  return null;
}

/** Un solo link de navegación, reutilizado por el nav de escritorio y el drawer mobile. */
function SettingsNavLink({
  item,
  active,
  className,
  currentPathname,
  onNavigate,
}: {
  item: NavItem;
  active: boolean;
  className: string;
  currentPathname: string;
  onNavigate?: () => void;
}) {
  if (item.kind === "hash") {
    return (
      <NavLink
        to={{ pathname: item.to, hash: `#${item.hash}` }}
        className={() => className}
        aria-current={active ? "page" : undefined}
        onClick={(e) => {
          onNavigate?.();
          if (currentPathname === item.to) {
            e.preventDefault();
            document.getElementById(item.hash)?.scrollIntoView({ behavior: "smooth" });
          }
        }}
      >
        {item.label}
      </NavLink>
    );
  }
  const to = itemHref(item);
  return (
    <NavLink
      to={typeof to === "string" ? to : to}
      end={item.kind === "path" ? item.end : false}
      className={() => className}
      aria-current={active ? "page" : undefined}
      onClick={() => onNavigate?.()}
    >
      {item.label}
    </NavLink>
  );
}

export function SettingsLayout() {
  const location = useLocation();
  const outletCtx = useOutletContext<DashboardOutletContext | undefined>();
  const plan = outletCtx?.plan ?? "FREE";
  const GROUPS = useMemo(() => buildSettingsNavGroups(plan), [plan]);
  const shouldReduceMotion = useReducedMotion();
  const [mobileOpen, setMobileOpen] = useState(false);

  const loc = { pathname: location.pathname, hash: location.hash, search: location.search };
  const activeItem = useMemo(() => findActiveItem(GROUPS, loc), [GROUPS, loc.pathname, loc.hash, loc.search]);

  // Cierra el drawer al navegar
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname, location.search, location.hash]);

  useEffect(() => {
    if (mobileOpen) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  const linkClass = (active: boolean) =>
    cx(
      css({
        display: "block",
        fontSize: "sm",
        py: "2",
        px: "3",
        rounded: "md",
        textDecoration: "none",
        color: active ? "token(colors.brand.700)" : "#64748b",
        fontWeight: active ? "semibold" : "medium",
        bg: active ? "rgba(99, 102, 241, 0.12)" : "transparent",
        _hover: { color: "#0f172a", bg: "rgba(15, 23, 42, 0.04)" },
      }),
    );

  // Estilo oscuro, consistente con el drawer del sidebar principal (DashboardLayout).
  const mobileLinkClass = (active: boolean) =>
    css({
      display: "block",
      fontSize: "sm",
      py: "2.5",
      px: "3",
      rounded: "md",
      textDecoration: "none",
      color: active ? "white" : "rgba(255,255,255,0.68)",
      fontWeight: active ? "semibold" : "medium",
      bg: active ? "rgba(255,255,255,0.1)" : "transparent",
      boxShadow: active ? "inset 2px 0 0 rgba(255,255,255,0.6)" : "none",
      transition: "opacity 120ms ease, background 120ms ease, transform 160ms cubic-bezier(0.23, 1, 0.32, 1)",
      _active: { transform: "scale(0.98)" },
    });

  return (
    <div
      className={css({
        display: "grid",
        gridTemplateColumns: { base: "1fr", lg: "240px 1fr" },
        gap: { base: "4", lg: "8" },
        alignItems: "start",
        maxW: "1200px",
        mx: "auto",
        w: "100%",
      })}
    >
      <nav
        className={cx(
          cf.dashSurface,
          css({ display: { base: "none", lg: "block" }, p: "4", position: { lg: "sticky" }, top: { lg: "4" } }),
        )}
      >
        {GROUPS.map((g) => (
          <div key={g.title} className={css({ mb: "6", _last: { mb: "0" } })}>
            <p className={css({ fontSize: "10px", fontWeight: "bold", letterSpacing: "widest", color: "#94a3b8", mb: "2" })}>
              {g.title}
            </p>
            <ul className={css({ display: "flex", flexDir: "column", gap: "0.5" })}>
              {g.items.map((item) => {
                const active = computeNavActive(loc, item);
                return (
                  <li key={item.key}>
                    <SettingsNavLink
                      item={item}
                      active={active}
                      className={linkClass(active)}
                      currentPathname={location.pathname}
                    />
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>

      <div className={css({ minW: 0, w: "100%" })}>
        {/* Trigger: reemplaza el <select> nativo en mobile/tablet (<1024px) */}
        <button
          type="button"
          onClick={() => setMobileOpen(true)}
          aria-haspopup="dialog"
          aria-expanded={mobileOpen}
          className={cx(
            cf.dashSurface,
            css({
              display: { base: "flex", lg: "none" },
              w: "full",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "3",
              px: "4",
              py: "3",
              mb: "4",
              cursor: "pointer",
              transition: "transform 160ms cubic-bezier(0.23, 1, 0.32, 1)",
              _active: { transform: "scale(0.99)" },
            }),
          )}
        >
          <span className={css({ display: "flex", flexDir: "column", gap: "0.5", minW: 0, textAlign: "left" })}>
            <span className={css({ fontSize: "10px", fontWeight: "bold", letterSpacing: "widest", color: "#94a3b8" })}>
              SECCIÓN
            </span>
            <span
              className={css({
                fontSize: "sm",
                fontWeight: "semibold",
                color: "#0f172a",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              })}
            >
              {activeItem?.label ?? "Elegí una sección"}
            </span>
          </span>
          <ChevronDown size={16} className={css({ color: "#64748b", flexShrink: 0 })} aria-hidden />
        </button>

        {/* Drawer/bottom-sheet: mismo lenguaje visual que el drawer del sidebar principal */}
        <AnimatePresence>
          {mobileOpen ? (
            <motion.div
              key="settings-sheet-root"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              style={{
                position: "fixed",
                inset: 0,
                zIndex: 45, // bajo el overlay del sidebar principal (z-50) para evitar conflicto
                display: "flex",
                alignItems: "flex-end",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  background: "rgba(0,0,0,0.55)",
                  backdropFilter: "blur(2px)",
                  WebkitBackdropFilter: "blur(2px)",
                }}
                onClick={() => setMobileOpen(false)}
              />

              <motion.div
                initial={{ y: shouldReduceMotion ? 0 : "100%" }}
                animate={{ y: 0 }}
                exit={{ y: shouldReduceMotion ? 0 : "100%" }}
                transition={shouldReduceMotion ? { duration: 0 } : { duration: 0.32, ease: [0.32, 0.72, 0, 1] }}
                role="dialog"
                aria-modal="true"
                aria-label="Elegir sección de configuración"
                className={cx(
                  cf.darkPanel,
                  css({
                    position: "relative",
                    zIndex: 1,
                    w: "full",
                    maxH: "80vh",
                    roundedTop: "2xl",
                    display: "flex",
                    flexDir: "column",
                    overflow: "hidden",
                  }),
                )}
              >
                <div className={css({ display: "flex", justifyContent: "center", pt: "2.5", flexShrink: 0 })}>
                  <span className={css({ w: "10", h: "1", rounded: "full", bg: "rgba(255,255,255,0.2)" })} />
                </div>

                <div
                  className={cx(
                    cf.darkPanelInner,
                    css({
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      px: "5",
                      pt: "2",
                      pb: "3",
                      borderBottomWidth: "1px",
                      borderColor: "rgba(255,255,255,0.08)",
                      flexShrink: 0,
                    }),
                  )}
                >
                  <p className={css({ fontSize: "sm", fontWeight: "bold", letterSpacing: "0.08em", color: "rgba(255,255,255,0.96)", m: 0 })}>
                    CONFIGURACIÓN
                  </p>
                  <button
                    type="button"
                    onClick={() => setMobileOpen(false)}
                    aria-label="Cerrar"
                    className={css({
                      w: "8",
                      h: "8",
                      rounded: "lg",
                      bg: "rgba(255,255,255,0.08)",
                      borderWidth: "1px",
                      borderColor: "rgba(255,255,255,0.16)",
                      color: "rgba(255,255,255,0.7)",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      transition: "opacity 120ms ease, transform 160ms cubic-bezier(0.23, 1, 0.32, 1)",
                      _hover: { opacity: 0.72 },
                      _active: { transform: "scale(0.93)" },
                    })}
                  >
                    <X size={16} aria-hidden />
                  </button>
                </div>

                <div
                  className={cx(
                    cf.darkPanelInner,
                    css({ overflowY: "auto", px: "5", py: "4", display: "flex", flexDir: "column", gap: "5" }),
                  )}
                >
                  {GROUPS.map((g) => (
                    <div key={g.title}>
                      <p
                        className={css({
                          fontSize: "10px",
                          fontWeight: "bold",
                          letterSpacing: "widest",
                          color: "rgba(255,255,255,0.4)",
                          mb: "2",
                        })}
                      >
                        {g.title}
                      </p>
                      <ul className={css({ display: "flex", flexDir: "column", gap: "0.5" })}>
                        {g.items.map((item) => {
                          const active = computeNavActive(loc, item);
                          return (
                            <li key={item.key}>
                              <SettingsNavLink
                                item={item}
                                active={active}
                                className={mobileLinkClass(active)}
                                currentPathname={location.pathname}
                                onNavigate={() => setMobileOpen(false)}
                              />
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  ))}
                </div>
              </motion.div>
            </motion.div>
          ) : null}
        </AnimatePresence>

        <Outlet context={outletCtx} />
      </div>
    </div>
  );
}
